/**
 * Browser-side HTTP for the BRC-169 handle endpoints, with no runtime
 * dependency on the BRC-169 library (and so none on `@bsv/sdk`): the account
 * prompt card and the site navigation import this module, and neither may pay
 * for the certificate machinery. Grammar and resolution live next door in
 * `handle-grammar.ts`.
 *
 * Browser-side HTTP for the BRC-169 handle route. Every request the handle
 * page, the account prompt card and the onboarding step make lives here, so
 * the exact wire contract of each endpoint is testable without React.
 *
 * Two transports, deliberately separate:
 *  - `fetcher` — the plain browser fetch, for the public availability and
 *    resolution endpoints and for the session-authenticated "my current
 *    allocation" read. None of these touch the wallet, so nothing in this file
 *    can raise a wallet prompt by itself.
 *  - `post` — a BRC-104 authenticated POST supplied by the wallet route. It is
 *    a plain function type on purpose: this module must stay importable from
 *    pages that are not allowed to reach the wallet SDK.
 */
import type { DiscoverabilityDirective, ForwardingRecord } from "@/lib/brc169";

const AVAILABILITY_PATH = "/api/brc169/handles/availability";
const CURRENT_PATH = "/api/brc169/handles/current";
export const ACTIVATIONS_PATH = "/api/brc169/handles/activations";
export const ALLOCATIONS_PATH = "/api/brc169/handles/allocations";
export const DISCOVERABILITY_PATH = "/api/brc169/handles/discoverability";
export const RELEASE_PATH = "/api/brc169/handles/release";

export type HandleClientErrorCode =
	| "conflict"
	| "forbidden"
	| "invalid"
	| "malformed"
	| "reassignment-pending"
	| "profile-handle-exists"
	| "unavailable";

/** A failure the page can explain. Never carries transport detail. */
export class HandleClientError extends Error {
	readonly code: HandleClientErrorCode;

	constructor(code: HandleClientErrorCode, message: string) {
		super(message);
		this.name = "HandleClientError";
		this.code = code;
	}
}

const PROBLEM_CODE_ERRORS: Record<string, HandleClientErrorCode> = {
	// A newer discoverability directive already won; the switch is behind, not
	// broken, so this is a conflict to explain rather than a service failure.
	DIRECTIVE_CONFLICT: "conflict",
	FORBIDDEN: "forbidden",
	HANDLE_CONFLICT: "conflict",
	HANDLE_NOT_RELEASABLE: "conflict",
	INVALID_FORWARDING_RECORD: "invalid",
	INVALID_HANDLE: "invalid",
	INVALID_REQUEST: "invalid",
	PREVIOUS_BINDING_REVOCATION_PENDING: "reassignment-pending",
	PROFILE_HANDLE_EXISTS: "profile-handle-exists",
	WALLET_BINDING_REQUIRED: "forbidden",
};

const ERROR_MESSAGES: Record<HandleClientErrorCode, string> = {
	conflict: "That handle is not available to claim.",
	forbidden:
		"This wallet is not linked to this profile, so it cannot manage its handle.",
	invalid: "Sigma refused that request as malformed.",
	malformed: "Sigma returned an answer it could not honor.",
	"reassignment-pending":
		"The previous holder's revocation is not confirmed spent yet. Try again shortly.",
	"profile-handle-exists": "This profile already has a current handle.",
	unavailable: "The handle service is unavailable right now. Try again later.",
};

function handleError(code: HandleClientErrorCode): HandleClientError {
	return new HandleClientError(code, ERROR_MESSAGES[code]);
}

/**
 * The flag decision, carried in the value rather than thrown. Every handle
 * endpoint answers the same RFC 9457 `NOT_FOUND` problem when BRC-169 handle
 * allocation is off for this deployment, and that is a state to render, not an
 * error to report.
 */
export type HandleFeature<T> = { enabled: false } | { enabled: true; value: T };

export interface HandleAvailability {
	handle: string;
	recipient: string;
	available: boolean;
	exactCollision: boolean;
	confusable: boolean;
}

export type HandleAllocationStatus = "reserved" | "active";

export interface ProfileHandleBinding {
	domain: string;
	handle: string;
	recipient: string;
	status: HandleAllocationStatus;
	registeredAt: string | null;
	createdAt: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isProblemNotFound(body: unknown): boolean {
	return isRecord(body) && body.code === "NOT_FOUND";
}

function problemError(body: unknown): HandleClientError {
	const code = isRecord(body) && typeof body.code === "string" ? body.code : "";
	return handleError(PROBLEM_CODE_ERRORS[code] ?? "unavailable");
}

async function readJson(response: Response): Promise<unknown> {
	try {
		return await response.json();
	} catch {
		return null;
	}
}

export async function fetchHandleAvailability(
	handle: string,
	fetcher: typeof fetch = fetch,
	signal?: AbortSignal
): Promise<HandleFeature<HandleAvailability>> {
	const response = await fetcher(
		`${AVAILABILITY_PATH}?handle=${encodeURIComponent(handle)}`,
		{ cache: "no-store", headers: { accept: "application/json" }, signal }
	);
	const body = await readJson(response);
	if (response.status === 404 && isProblemNotFound(body)) {
		return { enabled: false };
	}
	if (!response.ok) {
		throw problemError(body);
	}
	if (
		!isRecord(body) ||
		typeof body.handle !== "string" ||
		typeof body.recipient !== "string" ||
		typeof body.available !== "boolean" ||
		typeof body.exactCollision !== "boolean" ||
		!isRecord(body.warnings) ||
		typeof body.warnings.confusable !== "boolean"
	) {
		throw handleError("malformed");
	}
	return {
		enabled: true,
		value: {
			handle: body.handle,
			recipient: body.recipient,
			available: body.available,
			exactCollision: body.exactCollision,
			confusable: body.warnings.confusable,
		},
	};
}

function parseProfileBinding(value: unknown): ProfileHandleBinding {
	if (
		!isRecord(value) ||
		typeof value.domain !== "string" ||
		typeof value.handle !== "string" ||
		typeof value.recipient !== "string" ||
		(value.status !== "reserved" && value.status !== "active") ||
		!(value.registeredAt === null || typeof value.registeredAt === "string") ||
		typeof value.createdAt !== "string"
	) {
		throw handleError("malformed");
	}
	return {
		domain: value.domain,
		handle: value.handle,
		recipient: value.recipient,
		status: value.status,
		registeredAt: value.registeredAt,
		createdAt: value.createdAt,
	};
}

/**
 * This profile's current allocation, as the host knows it. It says whether a
 * handle is allocated and whether it is still only reserved; it never says the
 * holder's wallet has the certificate, which only the wallet can answer.
 */
export async function fetchCurrentProfileHandle(
	bapId: string,
	fetcher: typeof fetch = fetch,
	signal?: AbortSignal
): Promise<HandleFeature<ProfileHandleBinding | null>> {
	const response = await fetcher(
		`${CURRENT_PATH}?bapId=${encodeURIComponent(bapId)}`,
		{
			cache: "no-store",
			credentials: "include",
			headers: { accept: "application/json" },
			signal,
		}
	);
	const body = await readJson(response);
	if (response.status === 404 && isProblemNotFound(body)) {
		return { enabled: false };
	}
	if (!response.ok) {
		throw problemError(body);
	}
	if (!(isRecord(body) && "binding" in body)) {
		throw handleError("malformed");
	}
	return {
		enabled: true,
		value: body.binding === null ? null : parseProfileBinding(body.binding),
	};
}

/** A BRC-104 authenticated POST, supplied by the wallet-scoped route. */
export type Brc169AuthenticatedPost = (args: {
	origin: string;
	path: string;
	body: unknown;
}) => Promise<{ status: number; body: unknown }>;

export interface HandleAllocationResult {
	outcome: "created" | "existing";
	handle: string;
	recipient: string;
	status: HandleAllocationStatus;
	confusable: boolean;
}

/**
 * Claims one handle for this profile. A repeat of an allocation that already
 * exists answers 200 `existing` rather than a conflict, which is what makes a
 * failed certificate acquisition recoverable: the reservation survives and the
 * user can try adding it to the wallet again.
 */
export async function allocateHandle(
	input: { handle: string; origin: string },
	post: Brc169AuthenticatedPost
): Promise<HandleAllocationResult> {
	const { status, body } = await post({
		origin: input.origin,
		path: ALLOCATIONS_PATH,
		body: { handle: input.handle },
	});
	if (status !== 200 && status !== 201) {
		throw problemError(body);
	}
	if (
		!(isRecord(body) && isRecord(body.allocation)) ||
		typeof body.allocation.handle !== "string" ||
		typeof body.allocation.recipient !== "string" ||
		(body.allocation.status !== "reserved" &&
			body.allocation.status !== "active") ||
		(body.outcome !== "created" && body.outcome !== "existing")
	) {
		throw handleError("malformed");
	}
	return {
		outcome: body.outcome,
		handle: body.allocation.handle,
		recipient: body.allocation.recipient,
		status: body.allocation.status,
		confusable: isRecord(body.warnings) && body.warnings.confusable === true,
	};
}

export interface HandleActivationInput {
	origin: string;
	handle: string;
	certificateSerialNumber: string;
	revocationOutpoint: string;
	keyringForVerifier: { domain: string; handle: string };
	publicKeyring: { domain: string; handle: string };
}

/**
 * Moves a reserved binding to `active` (OPL-4365). The certificate must already
 * be in the wallet: both keyrings are encrypted under its serial number, so
 * this cannot happen until issuance has answered.
 */
export async function activateHandle(
	input: HandleActivationInput,
	post: Brc169AuthenticatedPost
): Promise<{ handle: string; recipient: string }> {
	const { status, body } = await post({
		origin: input.origin,
		path: ACTIVATIONS_PATH,
		body: {
			certificateSerialNumber: input.certificateSerialNumber,
			handle: input.handle,
			keyringForVerifier: input.keyringForVerifier,
			publicKeyring: input.publicKeyring,
			revocationOutpoint: input.revocationOutpoint,
		},
	});
	if (status !== 200) {
		throw problemError(body);
	}
	if (
		!(isRecord(body) && isRecord(body.binding)) ||
		typeof body.binding.handle !== "string" ||
		typeof body.binding.recipient !== "string" ||
		body.binding.status !== "active"
	) {
		throw handleError("malformed");
	}
	return {
		handle: body.binding.handle,
		recipient: body.binding.recipient,
	};
}

export type HandleReleaseResult =
	| { state: "released" }
	| { state: "revocation-pending" };

/**
 * Releases the profile's current handle. 204 means the binding is retired and
 * its certificate revocation is a confirmed spend; 202 means the revocation was
 * requested but its spend is not final, which is a real, honest intermediate
 * state and not a failure.
 */
export async function releaseHandle(
	input: { handle: string; origin: string; forwarding?: ForwardingRecord },
	post: Brc169AuthenticatedPost
): Promise<HandleReleaseResult> {
	const { status, body } = await post({
		origin: input.origin,
		path: RELEASE_PATH,
		body: input.forwarding
			? { handle: input.handle, forwarding: input.forwarding }
			: { handle: input.handle },
	});
	if (status === 204) {
		return { state: "released" };
	}
	if (status === 202) {
		return { state: "revocation-pending" };
	}
	throw problemError(body);
}

/**
 * Publishes the holder's signed discoverability directive (section 5.10). The
 * directive is signed by the wallet before it reaches here; this only carries
 * it. A 204 is the only success.
 */
export async function setHandleDiscoverability(
	input: { directive: DiscoverabilityDirective; origin: string },
	post: Brc169AuthenticatedPost
): Promise<void> {
	const { status, body } = await post({
		origin: input.origin,
		path: DISCOVERABILITY_PATH,
		body: input.directive,
	});
	if (status !== 204) {
		throw problemError(body);
	}
}
